import React, { createContext, useContext, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import api, { setAuth0Instance } from "../utils/axios";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const auth0 = useAuth0();

  // Set auth0 instance in axios interceptor
  useEffect(() => {
    if (auth0) {
      setAuth0Instance(auth0);
    }
  }, [auth0]);

  // Claim temporary leads after successful login
  useEffect(() => {
    const claimTemporaryLeads = async () => {
      if (auth0.isAuthenticated && auth0.user) {
        const claimSessionId = localStorage.getItem("hotlead_claim_session");

        if (claimSessionId) {
          try {
            const response = await api.post("/api/candidates/claim-temporary", {
              sessionId: claimSessionId,
              userId: auth0.user.sub,
            });

            if (response.data.success && response.data.totalLeads > 0) {
              // Successfully claimed leads - could add toast notification here
            }
          } catch (error) {
            console.error("Failed to claim temporary leads");
          } finally {
            // Clean up the claim session ID
            localStorage.removeItem("hotlead_claim_session");
            localStorage.removeItem("hotlead_temp_session");
          }
        }
      }
    };

    // Only run this when user becomes authenticated (not on every render)
    if (auth0.isAuthenticated && !auth0.isLoading) {
      claimTemporaryLeads();

      // Save any pending unauthenticated AI company search into user's dashboard
      (async () => {
        try {
          const pendingRaw = localStorage.getItem(
            "hotlead_pending_company_search"
          );
          if (!pendingRaw) return;
          const pending = JSON.parse(pendingRaw);
          if (!pending || !pending.results || !Array.isArray(pending.results)) {
            localStorage.removeItem("hotlead_pending_company_search");
            return;
          }

          // Persist the search results to backend as an agent record
          const body = {
            searchQuery: pending.userQuery || "AI Search",
            searchResults: pending.results,
            aiInterpretation: pending.aiInterpretation || null,
            sessionId: pending.sessionId || null,
            agentName: `AI Search: ${pending.userQuery || "Query"}`,
            leadType: "client",
          };

          const res = await api.post("/api/ai/save-search-results", body);
          if (res.data?.success) {
            // cleanup after successful save
            localStorage.removeItem("hotlead_pending_company_search");
          }
        } catch (err) {
          // Non-blocking: just log and keep the pending data
          console.error("Failed to save pending company search:", err?.message);
        }
      })();
    }
  }, [auth0.isAuthenticated, auth0.isLoading, auth0.user]);

  const value = {
    ...auth0,
    // Add any additional auth utilities here
    isLoading: auth0.isLoading,
    isAuthenticated: auth0.isAuthenticated,
    user: auth0.user,
    loginWithRedirect: auth0.loginWithRedirect,
    logout: auth0.logout,
    getAccessTokenSilently: auth0.getAccessTokenSilently,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
